import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Heart, Flame, Diamond, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { playReactionAnimation } from '../utils/reactionAnimations';
import SaveButton from './SaveButton';

const API = '/api';

const REACTIONS = [
  { type: 'heart', Icon: Heart, color: 'pink', label: 'Love' },
  { type: 'fire', Icon: Flame, color: 'orange', label: 'Fire' },
  { type: 'diamond', Icon: Diamond, color: 'cyan', label: 'Diamond' },
];

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return url;
  return `/${url}`;
};

const GalleryArtworkCard = ({ artwork }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const pendingBtnRef = useRef(null);

  const [myReaction, setMyReaction] = useState(artwork.user_reaction_type || null);
  const [reactionCounts, setReactionCounts] = useState(artwork.metrics?.reactions || {});
  const [reacting, setReacting] = useState(false);

  const imageUrl = resolveImageUrl(artwork.image_url || artwork.images?.[0]?.url);

  const handleReact = async (reactionType, e) => {
    e.stopPropagation();
    if (!user) { toast.error('Please sign in to react'); return; }
    if (reacting) return;
    setReacting(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('target_type', 'art_post');
      formData.append('target_id', artwork.id);
      formData.append('reaction_type', reactionType);

      const res = await axios.post(`${API}/react`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMyReaction(res.data.reaction_type);
      if (res.data.counts) setReactionCounts(res.data.counts);

      if ((res.data.action === 'added' || res.data.action === 'updated') && pendingBtnRef.current) {
        playReactionAnimation(pendingBtnRef.current, reactionType);
      }
      pendingBtnRef.current = null;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to react');
    } finally {
      setReacting(false);
    }
  };

  const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + b, 0);

  return (
    <div
      className="relative bg-gray-800 rounded-lg overflow-hidden cursor-pointer group"
      onClick={() => navigate(`/art/${artwork.id}`)}
      data-testid={`gallery-artwork-card-${artwork.id}`}
    >
      {/* Image */}
      <div className="aspect-square overflow-hidden relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={artwork.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Palette className="w-8 h-8 text-gray-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
          <p className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-semibold px-3 pb-2 truncate w-full">
            {artwork.title}
          </p>
        </div>
      </div>

      {/* Reaction bar + Save */}
      <div
        className="flex items-center px-1 py-1.5 bg-gray-900/95 border-t border-gray-700/50"
        data-testid={`gallery-reactions-${artwork.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-around flex-1">
          {REACTIONS.map(({ type, Icon, color, label }) => {
            const isSelected = myReaction === type;
            const count = reactionCounts[type] || 0;
            return (
              <button
                key={type}
                onClick={(e) => {
                  pendingBtnRef.current = e.currentTarget;
                  handleReact(type, e);
                }}
                disabled={reacting}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors disabled:opacity-50 ${
                  isSelected ? `text-${color}-400` : 'text-gray-500 hover:text-gray-300'
                }`}
                data-testid={`gallery-reaction-${type}-btn-${artwork.id}`}
                title={label}
                aria-label={`${label}${isSelected ? ' (selected)' : ''}`}
                aria-pressed={isSelected}
              >
                <Icon className={`w-3.5 h-3.5 ${isSelected && type === 'heart' ? 'fill-current' : ''}`} />
                {count > 0 && <span>{count}</span>}
              </button>
            );
          })}
        </div>
        <SaveButton
          targetId={artwork.id}
          targetType="artwork"
          initialSaved={artwork._saved}
          saveCount={artwork.save_count || 0}
          size="sm"
          className="px-1.5"
        />
      </div>
    </div>
  );
};

export default GalleryArtworkCard;
